/**
 * DataTable
 *
 * One generic list surface for the whole portal, replacing the per-page hand-rolled tables.
 *
 * Two render modes off the same column definitions:
 *  - `table`  (sm and up) — a real <Table> inside a <TableContainer> that scrolls HORIZONTALLY
 *                           INSIDE ITSELF. A wide column set never pushes the page sideways.
 *  - `cards`  (below sm)  — one <Card> per row: the `primary` column is the card title, the rest
 *                           render as label/value pairs, row actions become a button row.
 *
 * Paging is server-side when `total` is given (the parent fetches one page and passes it as
 * `rows`), and client-side otherwise (`rows` is everything and DataTable slices it). Sorting is
 * always server-side: DataTable renders the sort arrows and calls `onSortChange` with the
 * column's `sortKey`; the parent decides what that means.
 *
 *   <DataTable
 *     rows={users}
 *     columns={columns}
 *     getRowKey={(u) => u.id}
 *     page={page} pageSize={pageSize} total={data?.totalCount}
 *     onPageChange={setPage} onPageSizeChange={setPageSize}
 *     sortBy={sortBy} sortDescending={desc} onSortChange={handleSort}
 *     getRowActions={(u) => [{ key: 'edit', label: 'Edit', icon: <Edit />, onClick: () => edit(u) }]}
 *     toolbar={<SearchField value={search} onSearch={setSearch} />}
 *   />
 *
 * Prefer `getRowActions` over `rowActions`: it draws labelled buttons plus a "More" menu on a
 * phone card instead of a row of unlabelled icons (see RowActions). `rowActions` remains for a
 * row whose controls are not buttons (a switch, a select).
 *
 * On a phone there are no column headers to tap, so when any column is sortable a "Sort by"
 * select and a direction button appear above the cards.
 */

import { ReactNode, useMemo } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { ArrowDownward, ArrowUpward } from '@mui/icons-material'
import type { SxProps, Theme } from '@mui/material/styles'
import { RowActions, type RowAction } from './RowActions'

export interface DataTableColumn<T> {
  key: string
  header: ReactNode
  render: (row: T) => ReactNode
  /** Sort field name when sortable; omit for unsortable */
  sortKey?: string
  align?: 'left' | 'right' | 'center'
  width?: number | string
  /** Below this breakpoint the column is hidden in table mode. Default: always shown. */
  hideBelow?: 'sm' | 'md' | 'lg'
  /** In card mode (xs), whether this column is the card's title/primary line. Exactly one column should set this. */
  primary?: boolean
  /** In card mode, whether to show this column at all. Default true. */
  showInCard?: boolean
}

export interface DataTableProps<T> {
  rows: T[]
  columns: DataTableColumn<T>[]
  getRowKey: (row: T) => string
  loading?: boolean
  error?: string | null
  /** Shows a Retry button on the error. */
  onRetry?: () => void
  emptyMessage?: string
  /** Paging. If `total` is provided it's server-paged (page/pageSize/onPageChange required); otherwise client-paged over `rows`. */
  page: number // 0-based
  pageSize: number
  total?: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  pageSizeOptions?: number[] // default [10, 25, 50, 100]
  /** Sorting (server-side): current sort + handler. */
  sortBy?: string
  sortDescending?: boolean
  onSortChange?: (sortKey: string) => void
  /** Row click (whole row on desktop, whole card on mobile). */
  onRowClick?: (row: T) => void
  /** Per-row actions, rendered as an icon group in table mode and as a bottom row of buttons in card mode. */
  rowActions?: (row: T) => ReactNode
  /**
   * Per-row actions as data. Icon buttons with tooltips and aria-labels in table mode; labelled
   * buttons plus a "More" menu on a phone card. Drawn after `rowActions` when both are given.
   */
  getRowActions?: (row: T) => RowAction[]
  /** How many actions stay as labelled buttons on a phone card before "More". Default 2. */
  cardInlineActions?: number
  /**
   * Search box, filters, bulk controls: anything that belongs to the list. Rendered above it,
   * wrapping on a phone, so every page's search row looks and behaves the same.
   */
  toolbar?: ReactNode
  /** Force a mode; default: cards below `sm`, table at `sm` and up. */
  mode?: 'auto' | 'table' | 'cards'
  /**
   * Draw a row's card yourself in card mode, instead of the generic label/value stack.
   *
   * The generic stack is honest but tall — one labelled line per column turns a five-column
   * user into a screen-high card and pushes the pager out of reach. A page that knows its data
   * can lay a row out in two lines. `rowActions` and the row click still apply around it.
   */
  renderCard?: (row: T) => ReactNode
  /** Where the pager goes. `both` puts one above the list too, which matters on a phone. */
  pagerPosition?: 'bottom' | 'top' | 'both'
  /** Header styling: the portal uses a primary-coloured header row; keep that in table mode. */
  dense?: boolean
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

/** How many placeholder rows/cards to draw while loading. */
const SKELETON_ROWS = 5

/**
 * `hideBelow` is done in CSS rather than with a media-query hook, because the number of columns
 * (and therefore the number of hooks) is decided by the caller and can change between renders.
 */
function hideBelowSx(hideBelow?: 'sm' | 'md' | 'lg'): SxProps<Theme> | undefined {
  if (!hideBelow) return undefined
  return { display: { xs: 'none', [hideBelow]: 'table-cell' } } as SxProps<Theme>
}

export function DataTable<T>({
  rows,
  columns,
  getRowKey,
  loading = false,
  error = null,
  onRetry,
  emptyMessage = 'No results found',
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  sortBy,
  sortDescending = false,
  onSortChange,
  onRowClick,
  rowActions,
  getRowActions,
  cardInlineActions = 2,
  toolbar,
  mode = 'auto',
  dense = false,
  renderCard,
  pagerPosition = 'bottom',
}: DataTableProps<T>) {
  const showTopPager = pagerPosition === 'top' || pagerPosition === 'both'
  const showBottomPager = pagerPosition === 'bottom' || pagerPosition === 'both'
  const theme = useTheme()
  const isXs = useMediaQuery(theme.breakpoints.down('sm'))
  const cardMode = mode === 'cards' || (mode === 'auto' && isXs)

  // `total` present means the parent already paged the data server-side and `rows` IS the page.
  const isServerPaged = typeof total === 'number'
  const count = isServerPaged ? (total as number) : rows.length

  const pageRows = useMemo(() => {
    if (isServerPaged) return rows
    // Client paging. Clamp the offset so a shrinking result set (a filter that just got
    // narrower) shows the last page instead of an empty one.
    const maxPage = Math.max(0, Math.ceil(rows.length / pageSize) - 1)
    const safePage = Math.min(page, maxPage)
    const start = safePage * pageSize
    return rows.slice(start, start + pageSize)
  }, [isServerPaged, rows, page, pageSize])

  const primaryColumn = columns.find((c) => c.primary) ?? columns[0]
  const cardColumns = columns.filter((c) => c !== primaryColumn && c.showInCard !== false)

  const hasActions = Boolean(rowActions || getRowActions)

  // A clickable row is a button to the keyboard too: focusable, Enter/Space opens it. Keys
  // pressed inside the row's own controls (an action button, a switch) are theirs, not the row's.
  const rowButtonProps = (row: T) =>
    onRowClick
      ? {
          role: 'button',
          tabIndex: 0,
          onClick: () => onRowClick(row),
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.target !== e.currentTarget) return
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onRowClick(row)
            }
          },
        }
      : {}
  const sortableColumns = onSortChange ? columns.filter((c) => c.sortKey) : []

  const renderActions = (row: T, variant: 'icons' | 'labels') => (
    <>
      {rowActions?.(row)}
      {getRowActions && <RowActions actions={getRowActions(row)} variant={variant} inline={cardInlineActions} />}
    </>
  )

  const toolbarRow = toolbar ? (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        alignItems: 'center',
        mb: 1.5,
        '& > *': { minWidth: 0 },
      }}
    >
      {toolbar}
    </Box>
  ) : null

  const handleSort = (column: DataTableColumn<T>) => {
    if (!column.sortKey || !onSortChange) return
    onSortChange(column.sortKey)
  }

  // Changing the page size always returns to the first page — otherwise the caller lands on a
  // page number that no longer exists.
  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onPageSizeChange(parseInt(event.target.value, 10))
    onPageChange(0)
  }

  const pagination = (
    <TablePagination
      component="div"
      count={count}
      page={count > 0 ? Math.min(page, Math.max(0, Math.ceil(count / pageSize) - 1)) : 0}
      onPageChange={(_, newPage) => onPageChange(newPage)}
      rowsPerPage={pageSize}
      onRowsPerPageChange={handlePageSizeChange}
      rowsPerPageOptions={pageSizeOptions}
      // "Rows per page" alone eats most of a 390px screen.
      labelRowsPerPage={isXs ? 'Rows' : 'Rows per page'}
      sx={{
        borderTop: 1,
        borderColor: 'divider',
        '& .MuiTablePagination-toolbar': {
          px: { xs: 1, sm: 2 },
          flexWrap: 'wrap',
        },
        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
          mb: 0,
        },
      }}
    />
  )

  if (error) {
    return (
      <Box>
        {toolbarRow}
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            onRetry ? (
              <Button color="inherit" size="small" onClick={onRetry}>
                Retry
              </Button>
            ) : undefined
          }
        >
          {error}
        </Alert>
      </Box>
    )
  }

  // ===================
  // CARD MODE (phones)
  // ===================
  if (cardMode) {
    return (
      <Box>
        {toolbarRow}
        {sortableColumns.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
            <TextField
              select
              size="small"
              label="Sort by"
              value={sortableColumns.some((c) => c.sortKey === sortBy) ? sortBy : ''}
              onChange={(e) => onSortChange?.(e.target.value)}
              sx={{ flex: 1 }}
            >
              {sortableColumns.map((c) => (
                <MenuItem key={c.sortKey} value={c.sortKey}>
                  {typeof c.header === 'string' ? c.header : c.key}
                </MenuItem>
              ))}
            </TextField>
            <Tooltip title={sortDescending ? 'Descending' : 'Ascending'}>
              <span>
                {/* Re-selecting the active column is how every caller toggles direction. */}
                <IconButton
                  aria-label={sortDescending ? 'Sorted descending, tap for ascending' : 'Sorted ascending, tap for descending'}
                  disabled={!sortBy}
                  onClick={() => sortBy && onSortChange?.(sortBy)}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, width: 40, height: 40 }}
                >
                  {sortDescending ? <ArrowDownward fontSize="small" /> : <ArrowUpward fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
        {showTopPager && <Box sx={{ mb: 1 }}>{pagination}</Box>}
        {loading ? (
          <Stack spacing={1.5}>
            {Array.from({ length: Math.min(pageSize, SKELETON_ROWS) }).map((_, i) => (
              <Card key={i} variant="outlined">
                <CardContent>
                  <Skeleton variant="text" width="60%" height={28} />
                  <Skeleton variant="text" width="90%" />
                  <Skeleton variant="text" width="75%" />
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : pageRows.length === 0 ? (
          <Paper variant="outlined" sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">{emptyMessage}</Typography>
          </Paper>
        ) : (
          <Stack spacing={1}>
            {pageRows.map((row) => (
              <Card
                key={getRowKey(row)}
                variant="outlined"
                {...rowButtonProps(row)}
                sx={{
                  cursor: onRowClick ? 'pointer' : 'default',
                  '&:active': onRowClick ? { backgroundColor: 'action.selected' } : undefined,
                }}
              >
                {renderCard ? (
                  <Box sx={{ px: 1.5, py: 1.25 }}>{renderCard(row)}</Box>
                ) : (
                <CardContent sx={{ px: 1.5, pt: 1.25, pb: hasActions ? 0.5 : 1.25, '&:last-child': { pb: 1.25 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: cardColumns.length ? 0.5 : 0 }}>
                    {primaryColumn?.render(row)}
                  </Typography>

                  <Stack spacing={0.5}>
                    {cardColumns.map((column) => (
                      <Box
                        key={column.key}
                        sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ flexShrink: 0, textTransform: 'uppercase', letterSpacing: 0.4 }}
                        >
                          {column.header}
                        </Typography>
                        <Box sx={{ minWidth: 0, textAlign: 'right', typography: 'body2' }}>
                          {column.render(row)}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
                )}

                {hasActions && (
                  <>
                    <Divider />
                    <CardActions
                      sx={{ justifyContent: 'flex-end', gap: 0.5, py: 0.5 }}
                      // The card itself may be clickable; the action row must not trigger it.
                      onClick={(e) => e.stopPropagation()}
                    >
                      {renderActions(row, 'labels')}
                    </CardActions>
                  </>
                )}
              </Card>
            ))}
          </Stack>
        )}

        {showBottomPager && <Box sx={{ mt: 1 }}>{pagination}</Box>}
      </Box>
    )
  }

  // ===================
  // TABLE MODE (sm and up)
  // ===================
  const columnCount = columns.length + (hasActions ? 1 : 0)

  return (
    <Box>
    {toolbarRow}
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {showTopPager && pagination}
      {/* The ONLY place horizontal scrolling is allowed to happen. */}
      <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
        <Table size={dense ? 'small' : 'medium'} stickyHeader={false}>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              {columns.map((column) => {
                const sortable = Boolean(column.sortKey && onSortChange)
                const active = sortable && sortBy === column.sortKey

                return (
                  <TableCell
                    key={column.key}
                    align={column.align}
                    sortDirection={active ? (sortDescending ? 'desc' : 'asc') : false}
                    sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                      width: column.width,
                      ...hideBelowSx(column.hideBelow),
                    }}
                  >
                    {sortable ? (
                      <TableSortLabel
                        active={active}
                        direction={active && sortDescending ? 'desc' : 'asc'}
                        onClick={() => handleSort(column)}
                        // The header row is primary-coloured, so every state of the label
                        // (including the hover arrow) has to be forced white.
                        sx={{
                          color: 'white !important',
                          '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          '&:hover': { color: 'white' },
                        }}
                      >
                        {column.header}
                      </TableSortLabel>
                    ) : (
                      column.header
                    )}
                  </TableCell>
                )
              })}
              {hasActions && (
                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              Array.from({ length: Math.min(pageSize, SKELETON_ROWS) }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: columnCount }).map((__, j) => (
                    <TableCell key={j}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : pageRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((row) => (
                <TableRow
                  key={getRowKey(row)}
                  hover
                  {...rowButtonProps(row)}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align}
                      sx={{ width: column.width, ...hideBelowSx(column.hideBelow) }}
                    >
                      {column.render(row)}
                    </TableCell>
                  ))}
                  {hasActions && (
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
                        {renderActions(row, 'icons')}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {showBottomPager && pagination}
    </Paper>
    </Box>
  )
}
